/* MR Mouldings — set-up fee (Theme settings › MR Mouldings: set-up fee)
 *
 * The fee is a real cart line of a hidden product, one line per product whose Range (custom.range)
 * includes the configured value (Tulip), keyed by the hidden property _setup_fee_for = product handle.
 * Which lines trigger it is decided server-side by templates/cart.setup-fee.liquid.
 * Every fee is removed once the rest of the cart reaches the waiver threshold.
 *
 * reconcile() reads the cart, works out which fee lines it should hold, and adds or removes lines
 * until it does. It runs after every cart change in this tab (every add/change in the theme
 * dispatches cartcount:update) and before checkout, which catches anything that changed elsewhere
 * (another tab, the Back button). A no-op when the cart is already right, so it cannot loop.
 *
 * Without Shopify Plus nothing can enforce this at checkout; the Shopify Flow audit
 * (SETUP-FEE.md) catches any order that reaches checkout some other way.
 */
(function () {
  'use strict';
  var cfg = window.MRSetupFee;
  if (!cfg || !cfg.variantId) return;

  var isCartPage = document.body.classList.contains('template-cart');

  /* The cart page is server-rendered, so a fee change there needs a reload to show. Only ever
     follows something the shopper did (a quantity change, or pressing checkout). */
  function reloadCartPage() { window.location.reload(); }
  var running = null;
  var again = false;
  var broadcasting = false;

  function isTrigger(line) { return line.trigger === true; }
  function isFee(line) { return line.variant_id === cfg.variantId; }

  /* What the cart should hold, and what to change to get there. */
  function plan(cart) {
    var subtotal = cart.items_subtotal_price;
    var profiles = [];
    var titles = {};
    var fees = [];
    cart.items.forEach(function (line) {
      if (isFee(line)) { fees.push(line); subtotal -= line.final_line_price; return; }
      if (isTrigger(line) && !titles[line.handle]) { titles[line.handle] = line.product_title; profiles.push(line.handle); }
    });
    var waived = subtotal >= cfg.threshold;
    var wanted = waived ? [] : profiles;

    var updates = {};
    var changes = 0;
    var kept = {};
    fees.forEach(function (line) {
      var handle = line.properties && line.properties._setup_fee_for;
      if (handle && wanted.indexOf(handle) !== -1 && !kept[handle]) {
        kept[handle] = true;
        if (line.quantity !== 1) { updates[line.key] = 1; changes++; }
      } else {
        updates[line.key] = 0; changes++;
      }
    });
    var adds = wanted.filter(function (h) { return !kept[h]; }).map(function (h) {
      return { id: cfg.variantId, quantity: 1, properties: { _setup_fee_for: h, For: titles[h] } };
    });

    return {
      profiles: profiles.length,
      subtotal: subtotal,
      waived: waived && profiles.length > 0,
      remaining: Math.max(0, cfg.threshold - subtotal),
      updates: updates,
      adds: adds,
      changed: changes > 0 || adds.length > 0
    };
  }

  function json(url, body) {
    return fetch(url, body ? {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(body)
    } : { headers: { 'Accept': 'application/json' } }).then(function (r) {
      return r.json().then(function (d) {
        if (!r.ok) throw new Error((d && (d.description || d.message)) || 'Cart request failed');
        return d;
      });
    });
  }
  function getCart() { return json('/cart.js'); }
  // The cart with a per-line trigger flag (templates/cart.setup-fee.liquid)
  // No JSON Accept header: that could get the standard cart JSON back instead of the view.
  function getFeeCart() {
    return fetch('/cart?view=setup-fee', { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('Cart request failed');
      return r.text();
    }).then(function (t) { return JSON.parse(t); });
  }

  function broadcast(cart) {
    // Header count and cart-page totals listen for cartcount:update; the drawer ignores it for
    // open-on-add because of the marker, and is redrawn with mrcart:refresh instead.
    broadcasting = true;
    try {
      var detail = Object.assign({}, cart, { _mrSetupFee: true });
      window.dispatchEvent(new CustomEvent('cartcount:update', { detail: detail }));
      window.dispatchEvent(new CustomEvent('mrcart:refresh'));
    } catch (e) {}
    broadcasting = false;
  }

  /* Resolves to { changed, cart, state }. Serialised: calls during a run queue exactly one rerun. */
  function reconcile() {
    if (running) { again = true; return running; }
    running = getFeeCart().then(function (cart) {
      var p = plan(cart);
      if (!p.changed) return { changed: false, cart: cart, state: p };
      var step = Object.keys(p.updates).length ? json('/cart/update.js', { updates: p.updates }) : Promise.resolve();
      return step
        .then(function () { return p.adds.length ? json('/cart/add.js', { items: p.adds }) : null; })
        .then(function () { return Promise.all([getCart(), getFeeCart()]); })
        .then(function (carts) {
          broadcast(carts[0]);
          return { changed: true, cart: carts[0], state: plan(carts[1]) };
        });
    }).then(function (res) {
      running = null;
      if (again) { again = false; return reconcile().then(function (r2) { return { changed: res.changed || r2.changed, cart: r2.cart, state: r2.state }; }); }
      return res;
    }, function (err) {
      running = null; again = false;
      throw err;
    });
    return running;
  }

  cfg.plan = plan;
  cfg.reconcile = reconcile;

  /* After every cart change made anywhere in the theme. */
  window.addEventListener('cartcount:update', function () {
    if (broadcasting) return;
    reconcile().then(function (res) { if (res.changed && isCartPage) reloadCartPage(); }).catch(function () {});
  });


  /* Before checkout: if the fees were wrong, fix them and let the customer see the change first. */
  var verifiedSubmit = false;
  document.addEventListener('submit', function (e) {
    var submitter = e.submitter;
    if (verifiedSubmit || !submitter || submitter.name !== 'checkout') return;
    e.preventDefault();
    var form = e.target;
    reconcile().then(function (res) {
      if (res.changed) { if (isCartPage) reloadCartPage(); return; }
      verifiedSubmit = true;
      form.requestSubmit(submitter);
      verifiedSubmit = false;
    }, function () {
      // Could not read the cart: do not strand the customer. The Flow audit covers this order.
      verifiedSubmit = true;
      form.requestSubmit(submitter);
      verifiedSubmit = false;
    });
  }, true);
})();
